import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SectionTabs from '../../components/SectionTabs';
import { EVENTS_TABS } from '../../lib/section-tabs';
import { api, isAuthPending } from '../../lib/api';
import { clearSwrCacheByPrefix } from '../../lib/swrCache';
import { confirmDialog, promptDialog } from '../../lib/dialogs';
import type { EventSeries, EventStatus, ExtractedEvent, SourceType, Venue } from '../../types';
import EventEditModal from '../../components/EventEditModal';
import EventTagChips from '../../components/EventTagChips';
import EventReviewWarningChips from '../../components/EventReviewWarningChips';
import {
  DateProofWarning, dateProofRangeOptions, nonTemporalWarnings, useDateProofEvents, useDateProofReview,
} from '../../lib/dateProofReview';
import GroupBySelector from '../../components/GroupBySelector';
import { compareEventDates, eventTodayKey, formatRange } from '../../lib/eventDates';
import { DATE_WINDOW_ERROR, dateFilterWindow, matchesDateWindow } from '../../lib/eventFilters';
import { addCalendarDays } from '../../lib/eventTemporal';
import MergeEventsModal from '../../components/MergeEventsModal';
import { LoadingBlock } from '../../components/Spinner';
import { Pagination, usePagination } from '../../components/Pagination';
import {
  type EventGroupNode,
  type GroupByKey,
  groupByLabel,
  groupEvents,
} from '../../lib/eventGrouping';

type StatusFilter = EventStatus | 'review' | 'all';
type SortKey = 'date-asc' | 'date-desc' | 'title-asc' | 'venue-asc' | 'recently-added';

const STATUS_FILTERS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'all' },
  { value: 'review', label: 'Needs review' },
  { value: 'pending', label: 'pending' },
  { value: 'approved', label: 'approved' },
  { value: 'published', label: 'published' },
  { value: 'rejected', label: 'rejected' },
  { value: 'duplicate', label: 'duplicate' },
];

/** ISO date (yyyy-MM-dd) for the default server-side fetch window. We only
 *  pull events between [today - DEFAULT_BACK_DAYS, today + DEFAULT_FORWARD_DAYS]
 *  by default — cuts the admin payload from "every event ever" down to a
 *  bounded window. Users can widen the window by changing the date filters. */
const DEFAULT_BACK_DAYS = 90;
const DEFAULT_FORWARD_DAYS = 365;

function isoDateNDaysFromNow(deltaDays: number): string {
  return addCalendarDays(eventTodayKey(), deltaDays)!;
}

/** Server-fetch envelope returned by GET /api/mgmt/events. */
interface EventsListResponse {
  items: ExtractedEvent[];
  total: number;
  returned: number;
  limit: number;
  truncated: boolean;
  from: string;
  to: string;
}

interface Filters {
  search: string;
  location: string;
  dateFrom: string;
  dateTo: string;
  minConfidence: string;
  status: StatusFilter;
  sourceTypes: Set<SourceType>;
  venueIds: Set<string>;
  neighborhoods: Set<string>;
  eventTypes: Set<string>;
  tags: Set<string>;
  hasImage: boolean;
  hasTicket: boolean;
  isFreeOnly: boolean;
  sort: SortKey;
  /** Recurring filter. Default `'show-all'` shows everything. */
  recurringMode: 'show-all' | 'hide-instances' | 'only-repeats' | 'hide-weekly';
}

const DEFAULT_FILTERS: Filters = {
  search: '',
  location: '',
  dateFrom: isoDateNDaysFromNow(-DEFAULT_BACK_DAYS),
  dateTo: isoDateNDaysFromNow(DEFAULT_FORWARD_DAYS),
  minConfidence: '',
  status: 'all',
  sourceTypes: new Set(),
  venueIds: new Set(),
  neighborhoods: new Set(),
  eventTypes: new Set(),
  tags: new Set(),
  hasImage: false,
  hasTicket: false,
  isFreeOnly: false,
  sort: 'date-desc',
  recurringMode: 'show-all'
};

function uniqSorted(values: (string | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => !!v && v.trim().length > 0))).sort((a, b) =>
    a.localeCompare(b)
  );
}

function toggle<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

function tryParseArray(s?: string): string[] | undefined {
  if (!s) return undefined;
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : undefined; } catch { return undefined; }
}

/** Sanitise a group bucket value for use as part of a DOM id / class. */
function cssSafe(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 64);
}

export default function AdminEventsPage() {
  const [loadedItems, setItems] = useState<ExtractedEvent[]>([]);
  const items = useDateProofEvents(loadedItems);
  const { open: openDateProof } = useDateProofReview();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [series, setSeries] = useState<EventSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [groupBy, setGroupBy] = useState<GroupByKey[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<ExtractedEvent | null>(null);
  const [merging, setMerging] = useState<ExtractedEvent[] | null>(null);
  /** Tracks which row's inline editor is currently saving so we can disable just that select. */
  const [savingInline, setSavingInline] = useState<string | null>(null);
  /** Last server-fetch metadata, used to surface "showing N of M, narrow filters" hints. */
  const [serverMeta, setServerMeta] = useState<{ total: number; returned: number; limit: number; truncated: boolean; from: string; to: string } | null>(null);

  const load = async (status: StatusFilter, dateFrom: string, dateTo: string, search: string, location: string) => {
    setLoading(true);
    setError(null);
    try {
      const window = dateFilterWindow(dateFrom, dateTo);
      if (!window) throw new Error(DATE_WINDOW_ERROR);
      // Send dateFrom/dateTo to the server so it can scope to specific
      // partition months (yyyy-MM). The backend defaults to a sensible window
      // when these are omitted, but we always pass them now so the visible
      // dataset matches what the date filter shows.
      // Send free-text search/location server-side too — without this, the
      // server would truncate to its default 500-row cap before our search
      // ever ran, and we'd miss matches in the rest of the window.
      const params = new URLSearchParams({ status });
      if (window.from) params.set('from', window.from.toISOString());
      if (window.to) params.set('to', window.to.toISOString());
      if (search.trim()) params.set('q', search.trim());
      if (location.trim()) params.set('loc', location.trim());
      const res = await api.get<EventsListResponse | ExtractedEvent[]>(`/api/mgmt/events?${params}`);
      // Backwards compatibility: older deploys returned a bare array.
      const list = Array.isArray(res) ? res : res.items;
      const meta = Array.isArray(res)
        ? { total: res.length, returned: res.length, limit: res.length, truncated: false, from: dateFrom, to: dateTo }
        : { total: res.total, returned: res.returned, limit: res.limit, truncated: res.truncated, from: res.from, to: res.to };
      const parsed = list.map((e) => {
        const anyE = e as ExtractedEvent & { tagsJson?: string; performersJson?: string };
        return {
          ...e,
          tags: e.tags ?? tryParseArray(anyE.tagsJson),
          performers: e.performers ?? tryParseArray(anyE.performersJson)
        };
      });
      parsed.sort((a, b) => compareEventDates(a, b, -1));
      setItems(parsed);
      setServerMeta(meta);
      setSelected(new Set());
    } catch (e) {
      if (!isAuthPending(e)) setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  // Refetch from the server whenever the inputs that affect the SQL/OData
  // window or server-side text filter change: status, dateFrom, dateTo,
  // search, location. Search/location are debounced via `debouncedSearch`
  // so we don't fire a request on every keystroke. The remaining filters
  // (venue / tag / source / etc.) stay client-side and operate on the
  // loaded window.
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const [debouncedLocation, setDebouncedLocation] = useState(filters.location);
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(filters.search), 350);
    return () => window.clearTimeout(t);
  }, [filters.search]);
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedLocation(filters.location), 350);
    return () => window.clearTimeout(t);
  }, [filters.location]);

  useEffect(() => {
    void load(filters.status, filters.dateFrom, filters.dateTo, debouncedSearch, debouncedLocation);
  }, [filters.status, filters.dateFrom, filters.dateTo, debouncedSearch, debouncedLocation]);

  useEffect(() => {
    api.get<Venue[]>('/api/mgmt/venues').then(setVenues).catch(() => setVenues([]));
  }, []);

  useEffect(() => {
    api.get<EventSeries[]>('/api/mgmt/series').then(setSeries).catch(() => setSeries([]));
  }, []);

  const seriesById = useMemo(() => new Map(series.map((s) => [s.rowKey, s])), [series]);

  const venueById = useMemo(() => new Map(venues.map((v) => [v.rowKey, v])), [venues]);

  // Hydrate venueName/neighborhood/category locally — admin endpoint returns
  // raw entities (no in-memory enrichment), so we mirror what the public
  // endpoint does to make filtering consistent.
  const enriched = useMemo(
    () =>
      items.map((e) => {
        const v = e.venueId ? venueById.get(e.venueId) : undefined;
        return {
          ...e,
          venueName: e.venueName ?? v?.name,
          neighborhood: e.neighborhood ?? v?.partitionKey,
          category: e.category ?? v?.category ?? e.sourceType
        };
      }),
    [items, venueById]
  );

  const allSourceTypes = useMemo(() => uniqSorted(enriched.map((e) => e.sourceType)) as SourceType[], [enriched]);
  const allVenues = useMemo(
    () =>
      Array.from(
        new Map(
          enriched.filter((e) => e.venueId && e.venueName).map((e) => [e.venueId!, { id: e.venueId!, name: e.venueName! }])
        ).values()
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [enriched]
  );
  const allNeighborhoods = useMemo(() => uniqSorted(enriched.map((e) => e.neighborhood)), [enriched]);
  const allEventTypes = useMemo(() => uniqSorted(enriched.map((e) => e.eventType)), [enriched]);
  const allTags = useMemo(() => uniqSorted(enriched.flatMap((e) => e.tags ?? [])), [enriched]);

  const filtered = useMemo<ExtractedEvent[]>(() => {
    const q = filters.search.trim().toLowerCase();
    const loc = filters.location.trim().toLowerCase();
    const minConf = filters.minConfidence === '' ? null : Number(filters.minConfidence);
    const window = dateFilterWindow(filters.dateFrom, filters.dateTo);
    if (!window) return [];
    const requiredTags = filters.tags.size ? [...filters.tags] : null;

    let list = enriched.filter((e) => {
      if (q) {
        const hay = `${e.title} ${e.description ?? ''} ${e.organizer ?? ''} ${e.venueName ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (loc) {
        const hay = `${e.locationText ?? ''} ${e.venueName ?? ''} ${e.neighborhood ?? ''}`.toLowerCase();
        if (!hay.includes(loc)) return false;
      }
      if (filters.sourceTypes.size && !filters.sourceTypes.has(e.sourceType)) return false;
      if (filters.venueIds.size && !(e.venueId && filters.venueIds.has(e.venueId))) return false;
      if (filters.neighborhoods.size && !(e.neighborhood && filters.neighborhoods.has(e.neighborhood))) return false;
      if (filters.eventTypes.size && !(e.eventType && filters.eventTypes.has(e.eventType))) return false;
      if (requiredTags && !(e.tags && requiredTags.every((t) => e.tags!.includes(t)))) return false;
      if (filters.hasImage && !e.imageUrl) return false;
      if (filters.hasTicket && !e.ticketUrl) return false;
      if (filters.isFreeOnly && !e.isFreeEntry) return false;
      // Recurring-event filter (mirrors public applyFilters logic).
      if (filters.recurringMode === 'hide-instances' && e.isRecurringInstance === true) return false;
      if (filters.recurringMode === 'hide-weekly') {
        if (e.isRecurringInstance === true) return false;
        if (e.recurringPattern && e.recurringPattern !== 'one-time') return false;
      }
      if (filters.recurringMode === 'only-repeats') {
        const isInstance = e.isRecurringInstance === true;
        const isRecurringAnchor = !!e.recurringPattern && e.recurringPattern !== 'one-time';
        if (!isInstance && !isRecurringAnchor) return false;
      }
      if (minConf !== null && !Number.isNaN(minConf) && e.confidence < minConf) return false;
      if (!matchesDateWindow(e, window)) return false;
      return true;
    });

    list = [...list];
    switch (filters.sort) {
      case 'date-asc':
        list.sort(compareEventDates);
        break;
      case 'date-desc':
        list.sort((a, b) => compareEventDates(a, b, -1));
        break;
      case 'title-asc':
        list.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'venue-asc':
        list.sort((a, b) => (a.venueName ?? '').localeCompare(b.venueName ?? ''));
        break;
      case 'recently-added':
        list.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
        break;
    }
    return list;
  }, [enriched, filters]);

  const filterCount =
    (filters.search ? 1 : 0) + (filters.location ? 1 : 0) + (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0) +
    (filters.minConfidence ? 1 : 0) + filters.sourceTypes.size + filters.venueIds.size +
    filters.neighborhoods.size + filters.eventTypes.size + filters.tags.size +
    (filters.hasImage ? 1 : 0) + (filters.hasTicket ? 1 : 0) + (filters.isFreeOnly ? 1 : 0) +
    (filters.recurringMode !== 'show-all' ? 1 : 0);

  const allFilteredIds = useMemo(() => filtered.map((e) => e.rowKey), [filtered]);
  const allFilteredSelected = allFilteredIds.length > 0 && allFilteredIds.every((id) => selected.has(id));
  const someFilteredSelected = allFilteredIds.some((id) => selected.has(id));
  const selectedEvents = useMemo(() => filtered.filter((e) => selected.has(e.rowKey)), [filtered, selected]);

  // Client-side pagination — keeps the page from locking up when the admin
  // endpoint returns hundreds/thousands of rows. Each row renders 3 native
  // <select>s, so rendering all of them at once is what was hanging the page.
  // Reset to page 1 whenever filters/sort/status change.
  const pagination = usePagination(filtered, {
    defaultPageSize: 50,
    storageKey: 'mh-admin-events-page-size',
    resetKey: filters,
  });
  const visible = useMemo(() => pagination.slice(filtered), [pagination, filtered]);

  const toggleOne = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllFiltered = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allFilteredSelected) for (const id of allFilteredIds) next.delete(id);
    else for (const id of allFilteredIds) next.add(id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());
  const resetFilters = () => setFilters((f) => ({ ...DEFAULT_FILTERS, status: f.status, sort: f.sort }));

  const bulkAct = async (action: 'approve' | 'reject' | 'delete', scope: 'selected' | 'filtered') => {
    const targets = scope === 'selected' ? selectedEvents : filtered;
    if (targets.length === 0) return;
    const verb = action === 'approve' ? 'Approve' : action === 'reject' ? 'Reject' : 'Permanently delete';
    if (!(await confirmDialog({
      title: `${verb} events`,
      body: `${verb} ${targets.length} event${targets.length === 1 ? '' : 's'}?`,
      confirmLabel: verb,
      danger: action !== 'approve',
    }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.post<{ requested: number; updated: number; failed: number }>(
        '/api/mgmt/events/bulk',
        { action, ids: targets.map((e) => ({ partitionKey: e.partitionKey, rowKey: e.rowKey })) }
      );
      setInfo(`${verb}d ${res.updated} of ${res.requested} event${res.requested === 1 ? '' : 's'}.${res.failed ? ` ${res.failed} failed.` : ''}`);
      await load(filters.status, filters.dateFrom, filters.dateTo, debouncedSearch, debouncedLocation);
    } catch (e) {
      if (!isAuthPending(e)) setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onSaved = (updated: ExtractedEvent) => {
    setItems((prev) => prev.map((e) => (e.rowKey === updated.rowKey ? { ...e, ...updated } : e)));
    clearSwrCacheByPrefix('events:');
  };

  /** Trigger an on-demand duplicate scan across all events (admin-only). */
  const runDedupScan = async () => {
    if (!(await confirmDialog({
      title: 'Run duplicate scan',
      body: 'Run a duplicate scan across all events now? This may take a minute.',
      confirmLabel: 'Run scan',
    }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.post<{ scanned: number; clusters: number; marked: number }>(
        '/api/mgmt/events/dedup-scan',
        {}
      );
      setInfo(
        `Dedup scan complete: scanned ${res.scanned ?? 0}, found ${res.clusters ?? 0} cluster${res.clusters === 1 ? '' : 's'}, marked ${res.marked ?? 0} as duplicate.`
      );
      clearSwrCacheByPrefix('events:');
      await load(filters.status, filters.dateFrom, filters.dateTo, debouncedSearch, debouncedLocation);
    } catch (e) {
      if (!isAuthPending(e)) setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Trigger the venue-identifier helper for one event. `force=true` retries
   * a previously-attempted event, otherwise the server short-circuits.
   * Refreshes the list so the new audit fields show up.
   */
  const runIdentifyVenueOne = async (e: ExtractedEvent, force = false) => {
    setSavingInline(e.rowKey);
    setError(null);
    try {
      await api.post(
        `/api/mgmt/events/${encodeURIComponent(e.partitionKey)}/${encodeURIComponent(e.rowKey)}/identify-venue`,
        { force }
      );
      setInfo(`Queued venue identifier for “${e.title}”. Refresh in a few seconds for the result.`);
    } catch (err) {
      if (!isAuthPending(err)) setError(String(err));
    } finally {
      setSavingInline(null);
    }
  };

  /**
   * Bulk-enqueue the venue-identifier helper for every event in the current
   * filter window that has no venue and has never been attempted. Honors the
   * server-side per-run cap (`venueIdentifyMaxPerRun`).
   */
  const runIdentifyVenuesBulk = async () => {
    if (!(await confirmDialog({
      title: 'Queue venue identifier',
      body: 'Queue the venue identifier for all unassigned, unattempted events in the current window?',
      confirmLabel: 'Queue',
    }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.post<{ scanned: number; enqueued: number; skippedAttempted: number; skippedHasVenue: number; enabled: boolean }>(
        '/api/mgmt/events/identify-venues',
        {}
      );
      if (!res.enabled) {
        setInfo(`Venue identifier is disabled in admin settings (would have queued ${res.enqueued} of ${res.scanned}).`);
      } else {
        setInfo(`Queued ${res.enqueued} event${res.enqueued === 1 ? '' : 's'} for venue identification (scanned ${res.scanned}, ${res.skippedAttempted} previously attempted, ${res.skippedHasVenue} already assigned).`);
      }
    } catch (err) {
      if (!isAuthPending(err)) setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Run the calendar-post classifier backfill across recent EventsRaw rows.
   * Idempotent on the server side — only touches rows never previously
   * classified. Batches larger than 50 are processed asynchronously over a
   * storage queue (returns immediately with `enqueued` count).
   */
  const runClassifyCalendarPosts = async () => {
    const raw = await promptDialog({
      title: 'Classify calendar posts',
      label: 'How many unclassified raw posts? (1–5000)',
      body:
        'Server scans last 90 days. Batches >50 run async over a queue (returns immediately) — ' +
        'refresh the calendar-posts page in a few minutes to see results.',
      defaultValue: '50',
      validate: (v) => {
        const n = Number(v);
        if (!Number.isFinite(n) || n < 1 || n > 5000) return 'Enter a number between 1 and 5000';
        return null;
      },
    });
    if (raw == null) return;
    const limit = Math.max(1, Math.min(5000, Math.floor(Number(raw)) || 0));
    if (!limit) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const res = await api.post<
        | { mode: 'sync'; processed: number; classified: number; calendarPosts: number; dryRun: boolean }
        | { mode: 'async'; enqueued: number; dryRun: false }
      >('/api/mgmt/events/classify-calendar-posts', { limit });
      if (res.mode === 'async') {
        setInfo(
          `Calendar-post backfill: enqueued ${res.enqueued} row${res.enqueued === 1 ? '' : 's'} for background classification. Check back in a few minutes.`
        );
      } else {
        setInfo(
          `Calendar-post backfill: scanned ${res.processed}, classified ${res.classified}, ${res.calendarPosts} flagged as calendar posts.`
        );
      }
    } catch (err) {
      if (!isAuthPending(err)) setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  /** Inline PATCH used by the row-level status / venue / series editors. */
  const inlinePatch = async (e: ExtractedEvent, patch: Partial<ExtractedEvent>) => {
    setSavingInline(e.rowKey);
    setError(null);
    try {
      const updated = await api.patch<ExtractedEvent>(
        `/api/mgmt/events/${encodeURIComponent(e.rowKey)}`,
        patch
      );
      // Hydrate venueName / seriesName client-side so the row reflects the
      // change without re-fetching the venues list.
      const venue = updated.venueId ? venueById.get(updated.venueId) : undefined;
      const ser = updated.seriesId ? seriesById.get(updated.seriesId) : undefined;
      const merged: ExtractedEvent = {
        ...e,
        ...updated,
        venueName: venue?.name ?? updated.venueName,
        neighborhood: venue?.partitionKey ?? updated.neighborhood,
        seriesName: ser?.name ?? updated.seriesName,
        seriesType: ser?.partitionKey ?? updated.seriesType,
      };
      setItems((prev) => prev.map((x) => (x.rowKey === e.rowKey ? merged : x)));
      clearSwrCacheByPrefix('events:');
    } catch (err) {
      if (!isAuthPending(err)) setError(String(err));
    } finally {
      setSavingInline(null);
    }
  };

  const onMerged = (winnerId: string, loserIds: string[]) => {
    setItems((prev) =>
      prev.map((e) =>
        loserIds.includes(e.rowKey)
          ? { ...e, status: 'duplicate' as EventStatus, duplicateOf: winnerId }
          : e
      )
    );
    clearSelection();
    setInfo(`Merged ${loserIds.length} event${loserIds.length === 1 ? '' : 's'} into the winner. Decision recorded for AI training.`);
    void load(filters.status, filters.dateFrom, filters.dateTo, debouncedSearch, debouncedLocation);
  };

  function renderEventsTable(rows: ExtractedEvent[]) {
    return (
      <table className="table table-hover align-middle">
        <thead>
          <tr>
            <th scope="col" style={{ width: '2.5rem' }}>
              <span className="visually-hidden">Select</span>
            </th>
            <th scope="col">Title</th>
            <th scope="col">When</th>
            <th scope="col">Venue</th>
            <th scope="col">Series</th>
            <th scope="col">Status</th>
            <th scope="col">Source</th>
            <th scope="col">Conf</th>
            <th scope="col" className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => {
            const checked = selected.has(e.rowKey);
            return (
              <tr key={e.rowKey} className={checked ? 'table-active' : undefined}>
                <td>
                  <div className="form-check">
                    <input
                      id={`ev-sel-${e.rowKey}`}
                      type="checkbox"
                      className="form-check-input"
                      checked={checked}
                      onChange={() => toggleOne(e.rowKey)}
                      aria-label={`Select ${e.title}`}
                    />
                  </div>
                </td>
                <td>
                  <div className="fw-semibold">
                    <label htmlFor={`ev-sel-${e.rowKey}`} className="mb-0">{e.title}</label>
                  </div>
                  <div className="small text-muted d-flex flex-wrap gap-2 align-items-center">
                    {e.eventType && <span>{e.eventType}</span>}
                    <EventReviewWarningChips warnings={nonTemporalWarnings(e.reviewWarnings)} />
                    <EventTagChips
                      event={e}
                      chipClassName="badge rounded-pill text-bg-light border"
                      className="d-flex flex-wrap gap-1 align-items-center position-relative"
                      onChange={onSaved}
                    />
                    {e.duplicateOf && <span className="text-warning">dup of {e.duplicateOf.slice(0, 8)}…</span>}
                  </div>
                </td>
                <td className="small">
                  <div className="d-flex flex-column align-items-start gap-1">
                  <time dateTime={e.startUtc}>
                    {formatRange(e, dateProofRangeOptions(e, { date: { weekday: undefined, month: 'short', day: 'numeric', year: 'numeric' } }))}
                  </time>
                  <DateProofWarning event={e} />
                  {!e.reviewWarnings?.some((warning) => warning === 'check-date' || warning === 'time-unverified') && (
                    <button type="button" className="btn btn-link mh-date-proof-link" onClick={() => openDateProof(e)}>
                      Evidence / history
                    </button>
                  )}
                  {e.dateFlaggedWrong && <span className="badge mh-wrong-date-chip">Wrong date</span>}
                  </div>
                </td>
                <td className="small mh-cell-venue">
                  {e.venueId ? (() => {
                    const v = venueById.get(e.venueId);
                    const isPending = v?.reviewStatus === 'pending';
                    return (
                      <>
                        <div className="d-flex flex-wrap align-items-center gap-1">
                          <Link to={`/venues/${encodeURIComponent(e.venueId)}`} className="fw-semibold">
                            {e.venueName ?? v?.name ?? e.venueId}
                          </Link>
                          {isPending && (
                            <span
                              className="badge text-bg-warning"
                              title="Auto-created by the venue identifier helper. Awaiting admin approval."
                            >
                              pending review
                            </span>
                          )}
                        </div>
                        <details className="mt-1">
                          <summary className="small text-muted mh-cell-venue__change-toggle">change</summary>
                          <select
                            className="form-select form-select-sm mt-1"
                            value={e.venueId}
                            disabled={savingInline === e.rowKey}
                            onChange={(ev) => void inlinePatch(e, { venueId: ev.target.value || undefined })}
                            aria-label={`Change venue for ${e.title}`}
                          >
                            <option value="">— No venue —</option>
                            {venues.map((vv) => (
                              <option key={vv.rowKey} value={vv.rowKey}>{vv.name}</option>
                            ))}
                          </select>
                        </details>
                        {e.neighborhood && <div className="text-muted">{e.neighborhood}</div>}
                      </>
                    );
                  })() : (
                    <>
                      <select
                        className="form-select form-select-sm"
                        value=""
                        disabled={savingInline === e.rowKey}
                        onChange={(ev) => void inlinePatch(e, { venueId: ev.target.value || undefined })}
                        aria-label={`Venue for ${e.title}`}
                        title="Assign a venue"
                      >
                        <option value="">— No venue —</option>
                        {venues.map((v) => (
                          <option key={v.rowKey} value={v.rowKey}>{v.name}</option>
                        ))}
                      </select>
                      <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                        {e.venueIdentifyAttemptedAt ? (
                          <>
                            <span className="text-muted" title={e.venueIdentifyReason ?? ''}>
                              Identifier: {e.venueIdentifyResult ?? 'attempted'}
                              {typeof e.venueIdentifyConfidence === 'number' && ` (${e.venueIdentifyConfidence})`}
                            </span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              disabled={savingInline === e.rowKey}
                              onClick={() => void runIdentifyVenueOne(e, true)}
                              title="Re-run the venue identifier helper for this event"
                            >
                              Retry
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            disabled={savingInline === e.rowKey}
                            onClick={() => void runIdentifyVenueOne(e, false)}
                            title="Use the LLM + Google Places helper to identify a venue"
                          >
                            Identify venue
                          </button>
                        )}
                      </div>
                      {e.locationText && (
                        <div className="text-muted small fst-italic">orig: {e.locationText}</div>
                      )}
                    </>
                  )}
                </td>
                <td className="small mh-cell-series">
                  <select
                    className="form-select form-select-sm"
                    value={e.seriesId ?? ''}
                    disabled={savingInline === e.rowKey}
                    onChange={(ev) => void inlinePatch(e, { seriesId: ev.target.value || undefined })}
                    aria-label={`Series for ${e.title}`}
                    title="Link to a series"
                  >
                    <option value="">— None —</option>
                    {series.map((s) => (
                      <option key={s.rowKey} value={s.rowKey}>{s.name}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className={`form-select form-select-sm mh-status-select mh-status-select--${e.status}`}
                    value={e.status}
                    disabled={savingInline === e.rowKey}
                    onChange={(ev) => void inlinePatch(e, { status: ev.target.value as EventStatus })}
                    aria-label={`Status for ${e.title}`}
                    title="Change status"
                  >
                    <option value="pending">pending</option>
                    <option value="approved">approved</option>
                    <option value="published">published</option>
                    <option value="rejected">rejected</option>
                    <option value="duplicate">duplicate</option>
                  </select>
                </td>
                <td className="small">{e.sourceType}</td>
                <td className="small">{typeof e.confidence === 'number' ? e.confidence.toFixed(2) : '—'}</td>
                <td className="text-end">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => setEditing(e)}
                    aria-label={`Edit ${e.title}`}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  function renderEventsTree(nodes: EventGroupNode[], depth: number) {
    return nodes.map((node) => {
      const headingId = `mgmt-grp-${depth}-${node.key}-${cssSafe(node.value)}`;
      const headerInner = (
        <>
          <span className="mh-event-group__title">
            <span className="text-muted small me-2">{groupByLabel(node.key)}:</span>
            {node.label}
          </span>
          <span className="mh-event-group__count">
            {node.count} event{node.count === 1 ? '' : 's'}
          </span>
        </>
      );
      const headerProps = { id: headingId, className: 'mh-event-group__header' };
      const header =
        depth === 0 ? <h2 {...headerProps}>{headerInner}</h2>
        : depth === 1 ? <h3 {...headerProps}>{headerInner}</h3>
        : depth === 2 ? <h4 {...headerProps}>{headerInner}</h4>
        : <h5 {...headerProps}>{headerInner}</h5>;
      return (
        <section
          key={`${depth}-${node.key}-${node.value}`}
          className={`mh-event-group mh-event-group--depth-${depth}`}
          aria-labelledby={headingId}
        >
          {header}
          {node.children.length > 0
            ? renderEventsTree(node.children, depth + 1)
            : renderEventsTable(node.events)}
        </section>
      );
    });
  }

  return (
    <SectionTabs ariaLabel="Events sections" items={EVENTS_TABS}>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h1 className="mb-0">Manage events</h1>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={runIdentifyVenuesBulk}
            disabled={busy}
            title="Use the LLM + Google Places helper to identify venues for unassigned events"
          >
            {busy ? 'Working…' : 'Identify venues'}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={runDedupScan}
            disabled={busy}
            title="Scan all events for duplicates and mark losers as duplicate"
          >
            {busy ? 'Working…' : 'Run dedup scan'}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={runClassifyCalendarPosts}
            disabled={busy}
            title="Classify recent raw posts to detect calendar / multi-event flyer posts"
          >
            {busy ? 'Working…' : 'Classify calendar posts'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {info && <div className="alert alert-success" role="status">{info}</div>}

      {serverMeta?.truncated && (
        <div className="alert alert-warning small mb-3" role="status">
          Showing the first <strong>{serverMeta.limit.toLocaleString()}</strong> of{' '}
          <strong>{serverMeta.total.toLocaleString()}</strong> events in this date window. Narrow the
          <strong> Start from</strong> / <strong>Start to</strong> range or pick a more specific{' '}
          <strong>Status</strong> to see older / additional events.
        </div>
      )}

      <section aria-labelledby="ev-filters-heading" className="card mb-3">
        <div className="card-body">
          <h2 id="ev-filters-heading" className="visually-hidden">Filters</h2>

          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label htmlFor="ev-search" className="form-label small fw-semibold mb-1">Search</label>
              <input
                id="ev-search"
                type="search"
                className="form-control form-control-sm"
                placeholder="Title, description, venue, organizer…"
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="ev-loc" className="form-label small fw-semibold mb-1">Location / venue / neighborhood</label>
              <input
                id="ev-loc"
                type="search"
                className="form-control form-control-sm"
                value={filters.location}
                onChange={(e) => setFilters((f) => ({ ...f, location: e.target.value }))}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="ev-from" className="form-label small fw-semibold mb-1">Start from</label>
              <input
                id="ev-from"
                type="date"
                className="form-control form-control-sm"
                value={filters.dateFrom}
                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              />
            </div>
            <div className="col-md-2">
              <label htmlFor="ev-to" className="form-label small fw-semibold mb-1">Start to</label>
              <input
                id="ev-to"
                type="date"
                className="form-control form-control-sm"
                value={filters.dateTo}
                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              />
            </div>
            <div className="col-md-1">
              <label htmlFor="ev-status" className="form-label small fw-semibold mb-1">Status</label>
              <select
                id="ev-status"
                className="form-select form-select-sm"
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value as StatusFilter }))}
              >
                {STATUS_FILTERS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="row g-2 mt-1">
            <div className="col-md-3">
              <label htmlFor="ev-conf" className="form-label small fw-semibold mb-1">
                Min confidence: {filters.minConfidence === '' ? 'any' : Number(filters.minConfidence).toFixed(2)}
              </label>
              <input
                id="ev-conf"
                type="range"
                min="0"
                max="1"
                step="0.05"
                className="form-range"
                value={filters.minConfidence === '' ? '0' : filters.minConfidence}
                onChange={(e) => setFilters((f) => ({ ...f, minConfidence: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label htmlFor="ev-sort" className="form-label small fw-semibold mb-1">Sort by</label>
              <select
                id="ev-sort"
                className="form-select form-select-sm"
                value={filters.sort}
                onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value as SortKey }))}
              >
                <option value="date-desc">Date (latest first)</option>
                <option value="date-asc">Date (earliest first)</option>
                <option value="title-asc">Title A–Z</option>
                <option value="venue-asc">Venue A–Z</option>
                <option value="recently-added">Recently added</option>
              </select>
            </div>
            <div className="col-md-3">
              <label htmlFor="ev-recurring" className="form-label small fw-semibold mb-1">Recurring events</label>
              <select
                id="ev-recurring"
                className="form-select form-select-sm"
                value={filters.recurringMode}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    recurringMode: e.target.value as Filters['recurringMode']
                  }))
                }
                title="Control how weekly/recurring events appear"
              >
                <option value="show-all">Show all repeats (default)</option>
                <option value="hide-instances">Hide repeats</option>
                <option value="only-repeats">Only repeat events</option>
                <option value="hide-weekly">Hide weekly events</option>
              </select>
            </div>
            <div className="col-md-6 d-flex flex-wrap gap-3 align-items-end pb-1">
              <div className="form-check">
                <input
                  id="ev-img"
                  type="checkbox"
                  className="form-check-input"
                  checked={filters.hasImage}
                  onChange={(e) => setFilters((f) => ({ ...f, hasImage: e.target.checked }))}
                />
                <label htmlFor="ev-img" className="form-check-label small">Has image</label>
              </div>
              <div className="form-check">
                <input
                  id="ev-tix"
                  type="checkbox"
                  className="form-check-input"
                  checked={filters.hasTicket}
                  onChange={(e) => setFilters((f) => ({ ...f, hasTicket: e.target.checked }))}
                />
                <label htmlFor="ev-tix" className="form-check-label small">Has tickets</label>
              </div>
              <div className="form-check">
                <input
                  id="ev-free"
                  type="checkbox"
                  className="form-check-input"
                  checked={filters.isFreeOnly}
                  onChange={(e) => setFilters((f) => ({ ...f, isFreeOnly: e.target.checked }))}
                />
                <label htmlFor="ev-free" className="form-check-label small">Free entry</label>
              </div>
              {filterCount > 0 && (
                <button type="button" className="btn btn-link btn-sm ms-auto p-0" onClick={resetFilters}>
                  Clear {filterCount} filter{filterCount === 1 ? '' : 's'}
                </button>
              )}
            </div>
          </div>

          <div className="row g-3 mt-1">
            {allSourceTypes.length > 0 && (
              <div className="col-md-3">
                <label htmlFor="f-src" className="form-label small fw-semibold mb-1">Source</label>
                <select
                  id="f-src"
                  multiple
                  className="form-select form-select-sm mh-multi"
                  value={[...filters.sourceTypes]}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      sourceTypes: new Set(Array.from(e.target.selectedOptions, (o) => o.value as SourceType))
                    }))
                  }
                >
                  {allSourceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
            {allVenues.length > 0 && (
              <div className="col-md-3">
                <label htmlFor="f-venue" className="form-label small fw-semibold mb-1">Venue</label>
                <select
                  id="f-venue"
                  multiple
                  className="form-select form-select-sm mh-multi"
                  value={[...filters.venueIds]}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      venueIds: new Set(Array.from(e.target.selectedOptions, (o) => o.value))
                    }))
                  }
                >
                  {allVenues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            )}
            {allNeighborhoods.length > 0 && (
              <div className="col-md-2">
                <label htmlFor="f-hood" className="form-label small fw-semibold mb-1">Neighborhood</label>
                <select
                  id="f-hood"
                  multiple
                  className="form-select form-select-sm mh-multi"
                  value={[...filters.neighborhoods]}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      neighborhoods: new Set(Array.from(e.target.selectedOptions, (o) => o.value))
                    }))
                  }
                >
                  {allNeighborhoods.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
            {allEventTypes.length > 0 && (
              <div className="col-md-2">
                <label htmlFor="f-evtype" className="form-label small fw-semibold mb-1">Event type</label>
                <select
                  id="f-evtype"
                  multiple
                  className="form-select form-select-sm mh-multi"
                  value={[...filters.eventTypes]}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      eventTypes: new Set(Array.from(e.target.selectedOptions, (o) => o.value))
                    }))
                  }
                >
                  {allEventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
          </div>

          {allTags.length > 0 && (
            <fieldset className="mt-3">
              <legend className="form-label small fw-semibold mb-1">Tags</legend>
              <div className="d-flex flex-wrap gap-2">
                {allTags.map((t) => {
                  const active = filters.tags.has(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      className={`btn btn-sm mh-chip ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                      aria-pressed={active}
                      onClick={() => setFilters((f) => ({ ...f, tags: toggle(f.tags, t) }))}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          <div className="mt-3">
            <GroupBySelector value={groupBy} onChange={setGroupBy} />
          </div>
        </div>
      </section>

      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <div className="form-check me-2">
          <input
            id="ev-select-all"
            type="checkbox"
            className="form-check-input"
            checked={allFilteredSelected}
            ref={(el) => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
            onChange={toggleAllFiltered}
            disabled={filtered.length === 0}
          />
          <label htmlFor="ev-select-all" className="form-check-label small">
            Select all {filtered.length} filtered
          </label>
        </div>
        <span className="small text-muted">
          {selected.size} selected · showing {filtered.length} of {items.length}
        </span>
        {selected.size > 0 && (
          <button type="button" className="btn btn-link btn-sm p-0 ms-1" onClick={clearSelection}>
            Clear selection
          </button>
        )}
        <div className="ms-auto btn-group btn-group-sm" role="group" aria-label="Bulk actions">
          <button
            type="button"
            className="btn btn-success"
            disabled={busy || selectedEvents.length === 0}
            onClick={() => void bulkAct('approve', 'selected')}
          >
            Approve ({selectedEvents.length})
          </button>
          <button
            type="button"
            className="btn btn-outline-warning"
            disabled={busy || selectedEvents.length === 0}
            onClick={() => void bulkAct('reject', 'selected')}
          >
            Reject ({selectedEvents.length})
          </button>
          <button
            type="button"
            className="btn btn-outline-info"
            disabled={busy || selectedEvents.length < 2}
            onClick={() => setMerging(selectedEvents)}
            title={selectedEvents.length < 2 ? 'Select 2+ events to merge' : 'Merge selected events into one canonical record'}
          >
            Merge ({selectedEvents.length})
          </button>
          <button
            type="button"
            className="btn btn-outline-danger"
            disabled={busy || selectedEvents.length === 0}
            onClick={() => void bulkAct('delete', 'selected')}
          >
            Delete ({selectedEvents.length})
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingBlock label="Loading events…" />
      ) : filtered.length === 0 ? (
        <p className="text-muted">{items.length === 0 ? 'No events.' : 'No events match the current filters.'}</p>
      ) : groupBy.length > 0 ? (
        renderEventsTree(groupEvents(filtered, groupBy), 0)
      ) : (
        renderEventsTable(visible)
      )}

      {!loading && filtered.length > 0 && groupBy.length === 0 && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          totalItems={filtered.length}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
          pageSizeOptions={pagination.pageSizeOptions}
          ariaLabel="Events pagination"
          itemLabel="events"
        />
      )}

      <EventEditModal
        event={editing}
        venues={venues}
        seriesList={series}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />

      {merging && (
        <MergeEventsModal
          events={merging}
          venues={venues}
          onClose={() => setMerging(null)}
          onMerged={onMerged}
        />
      )}
    </SectionTabs>
  );
}
